import {
  type CareOrder,
  type Currency,
  type FundDefinition,
  getGroup,
  isCompound,
  isOption,
  isSpot,
  OrdTypeEnum,
  type PositionUpdateSourceEnum,
  type ProductTypeEnum,
  type QuoteMarket,
  RFQTradingProtocolEnum,
  type Security,
  SecurityTypesUtils,
  SideEnum,
  SubProductTypeEnum,
  toBig,
} from '@talos/kyoko';

export {
  careOrderToPrimeRFQParams,
  getParentOrderEndpoint,
  getParentTypeFromParentOrder,
  resolveParentTypeFromParentOrder,
} from './tradeCareOrder';

export const RFQ_SYMBOL_SELECTION_TOP_RIGHT_PORTAL = 'rfq-symbol-selection-top-right-portal';

export function showRFQFormTotalInHomeCcy(security: Security | undefined, homeCcy: Currency | undefined): boolean {
  if (!security || !homeCcy) {
    return false;
  }

  if (!isSpot(security)) {
    return false;
  }

  const homeCcyIsBaseOrQuote = [security.BaseCurrency, security.QuoteCurrency].includes(homeCcy.Symbol);
  if (homeCcyIsBaseOrQuote) {
    return false;
  }

  return true;
}

/** Returns whether or not the provided list of securities contains multiple product groups */
export function hasMultipleProductGroups(securities: Security[]): boolean {
  const foundGroups = new Set<string>();
  for (const security of securities) {
    const group = getGroup(security);
    foundGroups.add(group);
    if (foundGroups.size > 1) {
      return true;
    }
  }

  return false;
}

const FALCONX_MARKET_NAME = 'falconx';
export const STS_DIGITAL_MARKET_NAME = 'sts_digital';

export function isFalconXOption(security: Security | undefined): boolean {
  return (isOption(security) && security?.Markets?.includes(FALCONX_MARKET_NAME)) ?? false;
}

export function isStsDigitalOption(security: Security | undefined): boolean {
  return (isOption(security) && security?.Markets?.includes(STS_DIGITAL_MARKET_NAME)) ?? false;
}

export function isRFQSupportedOption(security: Security | undefined): boolean {
  return isFalconXOption(security) || isStsDigitalOption(security);
}

export function isEphemeralRFQSecurity(security: Security | undefined): boolean {
  return security?.SubProductType === SubProductTypeEnum.OTCRepo;
}

interface IsSecuritySelectableInRFQFormParams {
  security: Security | undefined;
  enableRFQFormFundSecurities: boolean;
  enableRFQFormOptions: boolean;
  enablePredictionMarkets?: boolean;
}

export function isSecuritySelectableInRFQForm({
  security,
  enableRFQFormFundSecurities,
  enableRFQFormOptions,
  enablePredictionMarkets = false,
}: IsSecuritySelectableInRFQFormParams): boolean {
  return (
    isSpot(security) ||
    (enableRFQFormFundSecurities && isCompound(security)) ||
    (enableRFQFormOptions && isRFQSupportedOption(security)) ||
    (enablePredictionMarkets && SecurityTypesUtils.PredictionOutcome.isProductType(security))
  );
}

interface ShouldDeArmTradeBestParams {
  ordType: OrdTypeEnum | undefined;
  /** The currently armed market to be sent if we click again */
  armedMarket: string | undefined;
  /** Upon new Quote revision, the new best market (might be different to armedMarket) */
  currentBestMarket: string | undefined;
}

/** Reusable definition of logic for deciding if we should de-arm an armed trade best button (double click to trade) */
export function shouldDeArmTradeBest({ ordType, armedMarket, currentBestMarket }: ShouldDeArmTradeBestParams): boolean {
  if (ordType !== OrdTypeEnum.RFQ || armedMarket == null) {
    return false;
  }

  return armedMarket !== currentBestMarket;
}

/** In the RFQSlice, we create a static Position request. */
export const RFQSLICE_POSITION_REQUEST_PARAMS: { AssetTypes: ProductTypeEnum[]; Source: PositionUpdateSourceEnum } = {
  AssetTypes: ['Option'], // we only support FalconX options for now in the RFQ form. As we add support for more types, we should add those here.
  Source: 'Gateway',
};

/** A fund is selectable if it has tradable securities defined and with at least one entry */
export function isFundSelectableInRFQForm(fund: FundDefinition): boolean {
  return fund.TradableSecurities != null && fund.TradableSecurities.length > 0;
}

export function getOrderQtyForQuoteAcceptance({
  side,
  childQuote,
  quoteOrderQty,
  explicitOrderQty,
  associatedCareOrder,
}: {
  side: SideEnum;
  childQuote: Pick<QuoteMarket, 'BidQty' | 'OfferQty'>;
  /** RFQ order quantity used when the quote leg does not provide a side-specific tradable qty. */
  quoteOrderQty: string;
  /** Explicit accept quantity from partial-fill/sweeper flows. */
  explicitOrderQty?: string;
  /** Parent care order (if coupled). Used to verify the qty to trade so we dont try to overfill the care order */
  associatedCareOrder: CareOrder | undefined;
}): string {
  let resolvedOrderQty: string;

  if (explicitOrderQty) {
    // User passed in a specific order qty, happens in partial fill case where the user entered eg "3" for their qty specifically.
    resolvedOrderQty = explicitOrderQty;
  } else {
    // Else, user did not specify any quantity, they are just trading the bid/offer qty
    // For size countering flows, venues can return side-specific tradable qty on the quote leg.
    // We prefer that qty, but fall back to the RFQ order qty when leg qty is missing.
    resolvedOrderQty =
      side === SideEnum.Buy
        ? (childQuote.OfferQty ?? quoteOrderQty)
        : side === SideEnum.Sell
          ? (childQuote.BidQty ?? quoteOrderQty)
          : quoteOrderQty;
  }

  // With the introduction of size countering and sweeping, we can end up in a situation where RFQs open within a CareOrder
  // can have greater tradable qty than the care order has leaves qty. This check below ensures that that we only trade at most the leaves qty (dont overfill care order)
  if (associatedCareOrder) {
    const resolvedOrderQtyBig = toBig(resolvedOrderQty);
    const leavesQtyBig = toBig(associatedCareOrder?.LeavesQty);
    if (resolvedOrderQtyBig && leavesQtyBig && leavesQtyBig.lt(resolvedOrderQtyBig)) {
      resolvedOrderQty = associatedCareOrder.LeavesQty;
    }
  }

  return resolvedOrderQty;
}

/**
 * Size countering is supported for Spot, and for Compound only when the instrument has multiple legs.
 * Single-token Compound instruments are intentionally excluded.
 */
export function securitySupportsSizeCountering(security: Security | undefined): boolean {
  if (!security) {
    return false;
  }

  if (isSpot(security)) {
    return true;
  }

  if (isCompound(security)) {
    return (security.Legs?.length ?? 0) > 1;
  }

  return false;
}

export function securityHasSizeCounteringFeatureFlagEnabled(
  security: Security | undefined,
  {
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
  }: {
    enableRFQSizeCounteringMultitokenCompound: boolean;
    enableRFQSizeCounteringSpot: boolean;
  }
): boolean {
  if (!securitySupportsSizeCountering(security)) {
    return false;
  }

  if (isSpot(security)) {
    return enableRFQSizeCounteringSpot;
  }

  if (isCompound(security)) {
    return enableRFQSizeCounteringMultitokenCompound;
  }

  return false;
}

/** Given the rfq slice state and a specific set of feature flags, determine if we should let the user sweep (split RFQs) or not */
export function isRFQSweepingAvailable({
  security,
  rfqTradingProtocol,
  enableRFQSweeping,
  enableRFQSizeCounteringMultitokenCompound,
  enableRFQSizeCounteringSpot,
}: {
  security: Security | undefined;
  rfqTradingProtocol: RFQTradingProtocolEnum | undefined;
  enableRFQSweeping: boolean;
  enableRFQSizeCounteringMultitokenCompound: boolean;
  enableRFQSizeCounteringSpot: boolean;
}): boolean {
  return (
    enableRFQSweeping &&
    rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated &&
    securityHasSizeCounteringFeatureFlagEnabled(security, {
      enableRFQSizeCounteringMultitokenCompound,
      enableRFQSizeCounteringSpot,
    })
  );
}
